import React, { memo } from "react";
import PropTypes from "prop-types";
import { styled } from "@mui/material/styles";
import { Icon } from "../Icons";
import { ContexualHelp } from "../Notifications";

/**
 * Styled button component with link-like appearance
 */
const StyledButton = styled("button")(({ theme }) => ({
  fontSize: "16px",
  fontWeight: 600,
  border: "none",
  textDecoration: "underline",
  cursor: "pointer",
  outline: "none",
  backgroundColor: "transparent",
  color: theme.palette.primary.main,
  padding: 0,
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(0.5),
  "@media (max-width: 500px)": {
    fontSize: "13px"
  },
  "&:disabled": {
    color: theme.palette.action.disabled,
    cursor: "default"
  }
}));

/**
 * LinkButton component that looks like a hyperlink but functions as a button
 * Supports optional icons before or after the text
 *
 * @param {Object} props - Component props
 * @returns {React.ReactElement} React component
 */
const LinkButtonBase = ({
  clickHandler,
  title = "Edit",
  className = "",
  editDisabled = false,
  ContexualHelpText = "",
  icon = null,
  iconPosition = "after",
  iconSize = "small",
  ...rest
}) => {
  const renderIcon = icon && <Icon name={icon} size={iconSize} color="primary" />;

  return (
    <ContexualHelp title={ContexualHelpText} isDisableHoverListener={!editDisabled}>
      <StyledButton
        className={className}
        onClick={clickHandler}
        disabled={editDisabled}
        type="button"
        aria-label={title}
        {...rest}
      >
        {iconPosition === "before" && renderIcon}
        {title}
        {iconPosition === "after" && renderIcon}
      </StyledButton>
    </ContexualHelp>
  );
};

LinkButtonBase.propTypes = {
  /** Handler function when button is clicked */
  clickHandler: PropTypes.func.isRequired,
  /** Button text */
  title: PropTypes.string,
  /** Additional class names */
  className: PropTypes.string,
  /** Whether the button is disabled */
  editDisabled: PropTypes.bool,
  /** Text to display in the contextual help tooltip */
  ContexualHelpText: PropTypes.string,
  /** Icon name to display */
  icon: PropTypes.string,
  /** Position of the icon relative to the text */
  iconPosition: PropTypes.oneOf(["before", "after"]),
  /** Size of the icon */
  iconSize: PropTypes.oneOf(["small", "medium", "large"])
};

export const LinkButton = memo(LinkButtonBase);
